// Service d'authentification lié à l'API Express

const _isLocal = typeof window !== 'undefined' && window.location.hostname === 'localhost'
export let API_URL = _isLocal
  ? ((import.meta as any).env?.VITE_API_URL || 'http://localhost:3001/api')
  : '/api'  // Production : toujours relatif, le middleware Vercel gère le routing

/**
 * Permet à l'app desktop (Electron) de pointer vers un backend explicite,
 * puisqu'elle n'a pas accès au proxy /api du middleware Vercel.
 */
export function setApiUrl(url: string): void {
  API_URL = url.trim().replace(/\/+$/, '')
}

let unauthorizedHandler: (() => void) | null = null

export function registerUnauthorizedHandler(cb: () => void): void {
  unauthorizedHandler = cb
}

interface AdminUser {
  id?: string
  username: string
  email?: string
  nom: string
  role: string
}

/**
 * Fonction utilitaire pour faire des appels API avec le token JWT
 */
export async function apiFetch(endpoint: string, options: RequestInit = {}): Promise<Response> {
  const token = localStorage.getItem('adminToken')
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((options.headers as Record<string, string>) || {})
  }
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers
  })

  if (response.status === 401) {
    console.warn('[AUTH] Session expirée — déconnexion.')
    clearSession()
    unauthorizedHandler?.()
  }

  return response
}

/**
 * Vérifie si un utilisateur existe dans la base de données
 * @param username - Nom d'utilisateur (insensible à la casse)
 * @returns Promise<boolean> - true si l'utilisateur existe
 */
export async function checkUserExists(username: string): Promise<boolean> {
  try {
    const response = await fetch(`${API_URL}/auth/check/${username.toLowerCase().trim()}`)
    if (!response.ok) return false
    
    const data = await response.json()
    return data.exists
  } catch (error) {
    console.error('Erreur réseau de vérification:', error)
    return false
  }
}

export async function validateSession(): Promise<AdminUser | null> {
  const token = localStorage.getItem('adminToken')
  if (!token) return null

  try {
    const response = await fetch(`${API_URL}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!response.ok) {
      clearSession()
      return null
    }

    const data = await response.json()
    if (data.user) {
      saveSession(data.user)
      return data.user
    }
  } catch (error) {
    console.error('Erreur de validation de session:', error)
  }

  return null
}

export type LoginResult =
  | { status: 'ok'; user: AdminUser }
  | { status: 'requires_2fa'; pendingId: string }
  | { status: 'error' }

/**
 * Authentifie un utilisateur avec son mot de passe.
 * Retourne 'requires_2fa' si le 2FA est activé (code OTP envoyé par email).
 */
export async function authenticateUser(username: string, password: string, turnstileToken = ''): Promise<LoginResult> {
  try {
    const response = await fetch(`${API_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        username: username.toLowerCase().trim(),
        password,
        cf_turnstile_response: turnstileToken,
      })
    })
    if (!response.ok) return { status: 'error' }
    const data = await response.json()
    if (data.requiresTwoFactor) return { status: 'requires_2fa', pendingId: data.pendingId }
    localStorage.setItem('adminToken', data.token)
    return { status: 'ok', user: data.user }
  } catch {
    return { status: 'error' }
  }
}

/**
 * Vérifie le code OTP reçu par email (étape 2FA).
 */
export async function verifyTwoFactor(pendingId: string, code: string): Promise<AdminUser | null> {
  try {
    const response = await fetch(`${API_URL}/auth/2fa/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pendingId, code })
    })
    if (!response.ok) return null
    const data = await response.json()
    localStorage.setItem('adminToken', data.token)
    return data.user
  } catch {
    return null
  }
}

/**
 * Stocke la session utilisateur
 * @param user - Informations utilisateur
 */
export function saveSession(user: AdminUser): void {
  if (typeof window !== 'undefined') {
    const sessionData = {
      username: user.username,
      nom: user.nom,
      role: user.role,
      timestamp: new Date().toISOString()
    }
    localStorage.setItem('adminSession', JSON.stringify(sessionData))
  }
}

/**
 * Récupère la session utilisateur
 * @returns AdminUser | null
 */
export function getSession(): AdminUser | null {
  if (typeof window !== 'undefined') {
    const sessionStr = localStorage.getItem('adminSession')
    if (sessionStr) {
      try {
        const sessionData = JSON.parse(sessionStr)
        return {
          username: sessionData.username,
          nom: sessionData.nom,
          role: sessionData.role
        }
      } catch (e) {
        return null
      }
    }
  }
  return null
}

/**
 * Supprime la session utilisateur (déconnexion)
 */
export function clearSession(): void {
  if (typeof window !== 'undefined') {
    localStorage.removeItem('adminSession')
    localStorage.removeItem('adminToken')
  }
}

/**
 * Vérifie si une session est active
 * @returns boolean
 */
export function hasActiveSession(): boolean {
  return getSession() !== null
}
